import type { SxProps, Theme } from '@mui/material';

/** The three-tone ramp every status chip is defined by. */
export interface StatusChipRamp {
  /** Pale background. */
  fill: string;
  /** Mid-tone. Rendered as the border. */
  border: string;
  /** Deep, saturated tone. Rendered as the ICON, not the label. */
  text: string;
}

/** Chip height. Matches the platform's `small` StatusChip (VERA-315). */
export const STATUS_CHIP_HEIGHT = 28;

/**
 * The one style rule for every status chip in `apps/app` — certificate statuses
 * (`StatusBadge`), scan outcomes (`ScanStatusChip`), and the seal-opened badge
 * (`TagOpenedBadge`).
 *
 * Extracted for VERA-628 (working-conventions §36): `StatusBadge` and `ScanStatusChip`
 * each hard-coded the same metrics, so every metric change had to be made twice and the
 * two had already begun to drift. One source now owns height, radius, padding, weight,
 * and the colour rule.
 *
 * **Colour rule (VERA-315, user directive): match the platform.** Pale `fill`, mid-tone
 * `border`, and the **deep tone as the LABEL** — `apps/platform`'s `StatusChip`
 * (`components/display/StatusChip`) renders `color: tone.color` on `alpha(color, .14)`,
 * and a brand operator sees both apps. Metrics follow it too: 28px tall, 12px, weight
 * 500, fully rounded, 12px label padding.
 *
 * ⚠️ THIS REVERSES A PRIOR USER DIRECTIVE, deliberately, and the earlier reasoning is
 * kept here because it is not wrong — it was answering a different question. VERA-628
 * specified a plain BLACK label, on the grounds that a deep-tone label on a pale fill is
 * lower contrast than black and made every chip read as an alert. That trade is real: the
 * platform's own chips are the lower-contrast option. What changed is the priority — one
 * status treatment across both apps was judged to matter more than the contrast the black
 * label bought. If the alert-y reading returns, darken the ramp's `text` tone rather than
 * going back to black, so the two apps stay in step.
 *
 * (The border took the DEEP tone once and read as outlined-in-ink at 1px against a pale
 * fill; the mid-tone is the settled value and is unchanged by the above.)
 */
export const statusChipSx = (
  ramp: StatusChipRamp,
  /**
   * True when the label is authored copy ("Opened", "Suspicious link") rather than a raw
   * status enum ("stolen"). Authored copy opts OUT of the theme's capitalize; a raw enum
   * still needs it ("stolen" -> "Stolen"). Getting this backwards either title-cases
   * mid-sentence words or leaves a status lowercase.
   */
  authoredLabel = false,
): SxProps<Theme> => ({
  height: `${STATUS_CHIP_HEIGHT}px`,
  borderRadius: '999px',
  p: 0,
  backgroundColor: ramp.fill,
  // Mid-tone outline; label stays black (see the colour rule above).
  //
  // `!important` is required, not defensive: the theme's `MuiChip.styleOverrides.root`
  // sets its own `border: 1px solid grey[600]`, and it was winning over this `sx` —
  // verified in the browser, where the chip computed to `rgb(229,229,229)` instead of
  // the ramp tone. Same class of override the `fontSize` and `textTransform` rules below
  // already need. (Note `apps/app` renders into a shadow root — `#vera-shadow-host` —
  // so emotion's insertion order differs from a normal document; don't assume plain
  // `sx` beats a theme `styleOverrides.root` here.)
  border: `1px solid ${ramp.border} !important`,
  color: ramp.text,
  '.MuiChip-label': {
    fontWeight: 500,
    fontSize: '12px !important',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    paddingLeft: '12px',
    paddingRight: '12px',
    // Deep tone, matching the platform. The icon inherits it via `color: inherit`
    // on the chip root, so the pill is now one colour throughout rather than a
    // coloured glyph beside a black word.
    color: ramp.text,
    // The theme capitalizes every MuiChip (settings/mui/MuiChip.ts, selector
    // `span.MuiChip-label` — specificity 0,1,1). Correct for a raw status enum
    // ("stolen" -> "Stolen"), wrong for an authored label: it title-cases mid-sentence
    // words ("Suspicious link" -> "Suspicious Link"). `!important` because this block's
    // `.MuiChip-label` (0,1,0) is outranked by the theme's.
    ...(authoredLabel ? { textTransform: 'none !important' } : {}),
  },
});
